import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserService from "../../services/userService";
import { handleApiError } from "../../api/handleApiError";

export const fetchDashboardSummary = createAsyncThunk(
  "user/fetchDashboardSummary",
  async (filters, thunkAPI) => {
    try {
      return (await UserService.getDashboardSummary(filters)).data.data;
    } catch {
      return thunkAPI.rejectWithValue("Summary Error");
    }
  },
);

export const fetchDashboardBonus = createAsyncThunk(
  "user/fetchDashboardBonus",
  async (_, thunkAPI) => {
    try {
      return (await UserService.getDashboardBonus()).data.data;
    } catch {
      return thunkAPI.rejectWithValue("Bonus Error");
    }
  },
);

export const fetchBonusIncentiveData = createAsyncThunk(
  "user/fetchBonusIncentiveData",
  async (_, thunkAPI) => {
    try {
      return (await UserService.getBonusIncentiveData()).data.data;
    } catch {
      return thunkAPI.rejectWithValue("Bonus Incentive Error");
    }
  },
);

export const fetchAllClients = createAsyncThunk(
  "user/fetchAllClients",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await UserService.getAllClients(params);

      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch {
      return thunkAPI.rejectWithValue({ key });
    }
  },
);

export const fetchAllCompanies = createAsyncThunk(
  "user/fetchAllCompanies",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await UserService.getAllCompanies(params);

      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch {
      return thunkAPI.rejectWithValue({ key });
    }
  },
);

export const fetchAllEmployees = createAsyncThunk(
  "user/fetchAllEmployees",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await UserService.getAllEmployees(params);

      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch {
      return thunkAPI.rejectWithValue({ key });
    }
  },
);

export const fetchUserById = createAsyncThunk(
  "user/fetchUserById",
  async ({ id, key }, thunkAPI) => {
    try {
      const res = await UserService.getUserById(id);

      return {
        key,
        data: res.data.data,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "User not found", key);
    }
  },
);

export const fetchCompanyById = createAsyncThunk(
  "user/fetchCompanyById",
  async ({ id, key }, thunkAPI) => {
    try {
      const res = await UserService.getCompanyById(id);

      return {
        key,
        data: res.data.data,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Company not found", key);
    }
  },
);

export const fetchEmployeeById = createAsyncThunk(
  "user/fetchEmployeeById",
  async ({ id, key }, thunkAPI) => {
    try {
      const res = await UserService.getEmployeeById(id);

      return {
        key,
        data: res.data.data,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Employee not found", key);
    }
  },
);

export const fetchAllDepartments = createAsyncThunk(
  "user/fetchAllDepartments",
  async (params) => {
    return (await UserService.getAllDepartments(params)).data.data || [];
  },
);

export const fetchAllDesignations = createAsyncThunk(
  "user/getAllDesignations",
  async (params) => {
    return (await UserService.getAllDesignations(params)).data.data || [];
  },
);

export const createClient = createAsyncThunk(
  "user/createClient",
  async (data, thunkAPI) => {
    try {
      return (await UserService.createClient(data)).data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error");
    }
  },
);

export const createCompany = createAsyncThunk(
  "user/createCompany",
  async (data, thunkAPI) => {
    try {
      return (await UserService.createCompany(data)).data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error");
    }
  },
);

export const createEmployee = createAsyncThunk(
  "user/createEmployee",
  async (data, thunkAPI) => {
    try {
      return (await UserService.createEmployee(data)).data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error");
    }
  },
);

export const updateCompany = createAsyncThunk(
  "user/updateCompany",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await UserService.updateCompany(id, data);

      if (res.data.errors && res.data.errors.length > 0) {
        return thunkAPI.rejectWithValue(res.data.errors);
      }

      return res.data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error");
    }
  },
);

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await UserService.updateUser(id, data);

      if (res.data.errors && res.data.errors.length > 0) {
        return thunkAPI.rejectWithValue(res.data.errors);
      }

      return res.data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error");
    }
  },
);

export const updateEmployee = createAsyncThunk(
  "user/updateEmployee",
  async ({ id, data }, thunkAPI) => {
    try {
      return (await UserService.updateEmployee(id, data)).data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error");
    }
  },
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async ({ id, roleName, key }, thunkAPI) => {
    try {
      await UserService.deleteUser(id, roleName);

      return {
        id,
        roleName,
        key,
      };
    } catch {
      return thunkAPI.rejectWithValue("Error");
    }
  },
);

export const deleteCompany = createAsyncThunk(
  "user/deleteCompany",
  async ({ id, key }, thunkAPI) => {
    try {
      await UserService.deleteCompany(id);

      return {
        id,
        key,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error deleting company");
    }
  },
);

export const deleteEmployee = createAsyncThunk(
  "user/deleteEmployee",
  async ({ id, key }, thunkAPI) => {
    try {
      return {
        id,
        key,
      };
    } catch {
      return thunkAPI.rejectWithValue("Error");
    }
  },
);

export const exportClientsCSV = createAsyncThunk(
  "user/exportClientsCSV",
  async (params) => {
    const res = await UserService.exportClientsCSV(params);

    downloadCSV(res.data, "clients.csv");

    return true;
  },
);

export const exportCompaniesCSV = createAsyncThunk(
  "user/exportCompaniesCSV",
  async (params) => {
    const res = await UserService.exportCompaniesCSV(params);

    downloadCSV(res.data, "companies.csv");

    return true;
  },
);

export const exportEmployeesCSV = createAsyncThunk(
  "user/exportEmployeesCSV",
  async (params) => {
    const res = await UserService.exportEmployeesCSV(params);

    downloadCSV(res.data, "employees.csv");

    return true;
  },
);

export const updateCompanyStatus = createAsyncThunk(
  "user/updateCompanyStatus",
  async (
    { companyId, isActive, kotapayCompanyId, kotapayPaymentId },
    thunkAPI,
  ) => {
    try {
      const res = await UserService.updateCompanyStatus({
        companyId,
        isActive,
        kotapayCompanyId,
        kotapayPaymentId,
      });

      return {
        companyId,
        isActive,
        data: res.data.data,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Error updating company status");
    }
  },
);

const downloadCSV = (data, fileName) => {
  const blob = new Blob([data], {
    type: "text/csv",
  });

  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;

  document.body.appendChild(link);

  link.click();

  link.remove();

  window.URL.revokeObjectURL(url);
};

/* =========================================================
   USER SLICE
   ========================================================= */

const initialState = {
  /*
   * IMPORTANT:
   *
   * NO token here.
   * NO refreshToken here.
   */

  user: null,
  role: null,

  isAuthenticated: false,

  // IMPORTANT
  authInitialized: false,

  dashboardSummary: null,
  dashboardBonus: null,
  bonusIncentiveData: null,

  listDataByKey: {},
  totalByKey: {},
  listLoadingByKey: {},

  entityByKey: {},
  entityLoadingByKey: {},
  entityErrorByKey: {},

  departmentList: [],
  designationList: [],

  loading: false,
  isSuccess: false,
  errorMessage: null,
  exportCSVLoading: false,
};

const userSlice = createSlice({
  name: "user",

  initialState,

  reducers: {
    /*
     * Called after successful login / /Me.
     */
    setUserData(state, action) {
      const { user } = action.payload;

      state.user = user || null;

      if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
        state.role = user.roles[0]?.roleName || null;
      } else {
        state.role = user?.role || null;
      }

      state.isAuthenticated = !!user;
    },

    setAuthInitialized(state, action) {
      state.authInitialized = action.payload;
    },
    /*
     * Clears only frontend Redux auth state.
     *
     * Actual cookie removal is handled by backend
     * through /Logout.
     */
    logout(state) {
      state.user = null;
      state.role = null;
      state.isAuthenticated = false;
      state.authInitialized = false;
      state.loading = false;
    },

    clearAuthState(state) {
      state.user = null;
      state.role = null;
      state.isAuthenticated = false;
      state.authInitialized = true;
      state.loading = false;
    },

    resetStatus(state) {
      state.isSuccess = false;
      state.errorMessage = null;
      state.loading = false;
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.dashboardSummary = action.payload;
      })

      .addCase(fetchDashboardBonus.fulfilled, (state, action) => {
        state.dashboardBonus = action.payload;
      })

      .addCase(fetchBonusIncentiveData.fulfilled, (state, action) => {
        state.bonusIncentiveData = action.payload;
      })

      .addCase(fetchAllDepartments.fulfilled, (state, action) => {
        state.departmentList = action.payload;
      })

      .addCase(fetchAllDesignations.fulfilled, (state, action) => {
        state.designationList = action.payload;
      })

      .addCase(deleteUser.fulfilled, (state, action) => {
        const { id, key } = action.payload;

        if (key && state.listDataByKey[key]) {
          state.listDataByKey[key] = state.listDataByKey[key].filter(
            (item) => item.id !== id,
          );

          state.totalByKey[key] -= 1;
        }

        if (key) {
          state.listLoadingByKey[key] = false;
        }
      })

      .addCase(deleteEmployee.fulfilled, (state, action) => {
        const { id, key } = action.payload;

        if (key && state.listDataByKey[key]) {
          state.listDataByKey[key] = state.listDataByKey[key].filter(
            (item) => item.id !== id,
          );

          state.totalByKey[key] -= 1;
        }

        if (key) {
          state.listLoadingByKey[key] = false;
        }
      })

      .addCase(deleteCompany.fulfilled, (state, action) => {
        const { id, key } = action.payload;

        if (key && state.listDataByKey[key]) {
          state.listDataByKey[key] = state.listDataByKey[key].filter(
            (item) => item.id !== id,
          );

          state.totalByKey[key] -= 1;
        }

        if (key) {
          state.listLoadingByKey[key] = false;
        }
      })

      .addCase(updateCompanyStatus.fulfilled, (state, action) => {
        const { companyId, isActive } = action.payload;

        const key = `companyDetail_${companyId}`;

        if (state.entityByKey[key]) {
          state.entityByKey[key] = {
            ...state.entityByKey[key],
            isActive,
          };
        }
      })

      .addMatcher(
        (action) => action.type.endsWith("/pending") && action.meta.arg?.key,

        (state, action) => {
          const key = action.meta.arg.key;

          state.listLoadingByKey[key] = true;

          state.entityLoadingByKey[key] = true;

          state.entityErrorByKey[key] = null;
        },
      )

      .addMatcher(
        (action) => action.type.endsWith("/fulfilled") && action.payload?.key,

        (state, action) => {
          const { key, data, totalRecords } = action.payload;

          if (Array.isArray(data)) {
            state.listDataByKey[key] = data;

            state.totalByKey[key] = totalRecords || 0;

            state.listLoadingByKey[key] = false;
          } else {
            state.entityByKey[key] = data;

            state.entityLoadingByKey[key] = false;
          }
        },
      )

      .addMatcher(
        (action) => action.type.endsWith("/rejected") && action.payload?.key,

        (state, action) => {
          const key = action.payload.key;

          state.listLoadingByKey[key] = false;

          state.entityLoadingByKey[key] = false;

          state.entityErrorByKey[key] = action.payload.error;
        },
      )

      .addMatcher(
        (action) =>
          (action.type.includes("create") ||
            action.type.includes("update") ||
            action.type.includes("export")) &&
          action.type.endsWith("/pending"),

        (state, action) => {
          state.loading = true;
          state.isSuccess = false;
          state.errorMessage = null;

          if (action.type.includes("export")) {
            state.exportCSVLoading = true;
          }
        },
      )

      .addMatcher(
        (action) =>
          (action.type.includes("create") ||
            action.type.includes("update") ||
            action.type.includes("export")) &&
          action.type.endsWith("/fulfilled"),

        (state) => {
          state.loading = false;
          state.isSuccess = true;
          state.errorMessage = null;
          state.exportCSVLoading = false;
        },
      )

      .addMatcher(
        (action) =>
          (action.type.includes("create") || action.type.includes("update")) &&
          action.type.endsWith("/rejected"),

        (state, action) => {
          state.loading = false;
          state.isSuccess = false;
          state.errorMessage = action.payload;
        },
      );
  },
});

export const {
  setUserData,
  logout,
  clearAuthState,
  setAuthInitialized,
  resetStatus,
} = userSlice.actions;

export default userSlice.reducer;
